import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, Image, Paperclip, CalendarPlus, BarChart3, UserPlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * WhatsApp/Slack-style single-level popover for chat attachment actions.
 * Opens above the trigger button; closes on outside click or Escape.
 */
const ChatAttachmentPopover = ({
  isOpen,
  onClose,
  anchorRef,
  onShareNote,
  onSharePhoto,
  onShareFile,
  onCreateEvent,
  onCreatePoll,
  onShareContact,
  disabled = false,
}) => {
  const { t } = useTranslation('chat');
  const menuRef = useRef(null);
  const [computedPosition, setComputedPosition] = useState({ x: 0, y: 0 });

  useLayoutEffect(() => {
    if (!isOpen || !anchorRef?.current) return;
    const anchor = anchorRef.current.getBoundingClientRect();
    const menuEl = menuRef.current;
    const menuHeight = menuEl?.offsetHeight ?? 280;
    const padding = 8;
    // Position above the button, aligned to the right edge of the button
    let x = anchor.right - 220; // menu width ~200
    let y = anchor.top - menuHeight - padding;
    if (x < padding) x = padding;
    if (y < padding) y = anchor.bottom + padding; // fallback below
    setComputedPosition({ x, y });
  }, [isOpen, anchorRef]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handlePointer = (e) => {
      if (e.target?.closest('[data-chat-attach-popover="true"]') || e.target?.closest('[data-chat-attach-trigger="true"]')) return;
      onClose?.(e);
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.(e);
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const items = useMemo(() => [
    { id: 'note', label: t('attachmentPopover.shareNote'), icon: FileText, onClick: onShareNote },
    { id: 'photo', label: t('attachmentPopover.sharePhoto'), icon: Image, onClick: onSharePhoto },
    { id: 'file', label: t('attachmentPopover.shareFile'), icon: Paperclip, onClick: onShareFile },
    { id: 'event', label: t('attachmentPopover.createEvent'), icon: CalendarPlus, onClick: onCreateEvent },
    { id: 'poll', label: t('attachmentPopover.createPoll'), icon: BarChart3, onClick: onCreatePoll },
    { id: 'contact', label: t('attachmentPopover.shareContact'), icon: UserPlus, onClick: onShareContact },
  ], [t, onShareNote, onSharePhoto, onShareFile, onCreateEvent, onCreatePoll, onShareContact]);

  if (!isOpen) return null;

  const popover = (
    <AnimatePresence>
      <motion.div
        ref={menuRef}
        data-chat-attach-popover="true"
        initial={{ opacity: 0, scale: 0.96, y: 4 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 4 }}
        transition={{ duration: 0.12 }}
        className="fixed z-[100010] w-[200px] overflow-hidden rounded-xl border border-white/[0.08] bg-[#0d1424] backdrop-blur-md shadow-2xl shadow-black/50"
        style={{ top: computedPosition.y, left: computedPosition.x }}
      >
        <div className="py-1.5">
          {items.map(({ id, label, icon: Icon, onClick }) => (
            <button
              key={id}
              type="button"
              onClick={() => {
                if (disabled) return;
                onClick?.();
                onClose?.();
              }}
              disabled={disabled}
              className="flex w-full items-center gap-3 px-3.5 py-2.5 text-sm font-medium transition-colors duration-150 text-[#c8d8eb] hover:bg-[#1e2d47] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
            >
              <Icon className="h-4 w-4 text-[#7a96b0] flex-shrink-0" strokeWidth={2.2} />
              <span className="text-left">{label}</span>
            </button>
          ))}
        </div>
      </motion.div>
    </AnimatePresence>
  );

  return createPortal(popover, document.body);
};

export default ChatAttachmentPopover;
